import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription, Subject } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { UserLoggedInModel } from '../models/user.model';
import { AuthModel } from '../models/auth.model';
import { AuthHTTPService } from './auth-http';
import { Router } from '@angular/router';
import { CookieComponent } from 'src/app/_metronic/kt/components/';
import { LoginResponse } from 'KiaLearn.Common/angular-common-libraries/projects/web-api-client/src/lib/generated/kia-authentication-api-client';
import { APIService } from 'src/app/api';
import { ProjectsService } from '../../projects/services/projects.service';

export type UserType = UserLoggedInModel | undefined;

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private authLocalStorageToken = `auth`;
  private logoutSubject = new Subject<void>();

  // public fields
  currentUser$: Observable<UserType>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserType>;
  isLoadingSubject: BehaviorSubject<boolean>;
  currentRole: string;

  // Observable that emits when user logs out (for cache clearing)
  logout$ = this.logoutSubject.asObservable();

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  get role(): string {
    if (!this.currentRole) {
      let storedRole = localStorage.getItem('role');
      if (storedRole) {
        this.currentRole = storedRole;
      }
    }

    return this.currentRole;
  }

  set role(role: string) {
    this.currentRole = role;
    localStorage.setItem('role', role);
  }

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router,
    private apiService: APIService,
    private projectService: ProjectsService
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserType>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    const subscr = this.getUserByToken().subscribe();
    this.unsubscribe.push(subscr);
  }

  // public methods
  login(email: string, password: string): Observable<UserType> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.login(email, password).pipe(
      map((auth: AuthModel) => {
        CookieComponent.delete('lessonProject');
        CookieComponent.delete('lessonCountry');
        const result = this.setAuthFromLocalStorage(auth);
        return result;
      }),
      switchMap(() => this.getUserByToken()),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  async loginWithAccessKey(email: string, loginResponse: LoginResponse, accessKey: string | undefined) {
    if (!loginResponse) {
      return;
    }
    
    this.role = loginResponse.role!;
    this.apiService.setAuth(loginResponse.token!);
    this.apiService.setLoggedInUserEmail(email);
    let redirectUrl = localStorage.getItem('redirectUrl');
    const expirationTime = new Date().getTime() + 12 * 60 * 60 * 1000;
    localStorage.setItem('accessKey', JSON.stringify({ key: accessKey, expires: expirationTime }));
    
    if (redirectUrl) {
      // set the project to localStorage
      if (accessKey) {
        const projectId = await this.projectService.getProjectIdByAccessKey(accessKey);
        
        if (projectId) {
          CookieComponent.set('lessonProject', projectId, {});
        }
      }

      // redirect
      this.router.navigateByUrl(redirectUrl);
      localStorage.removeItem('redirectUrl');
    } else {
      this.router.navigate(['/']);
    }
  }

  logout(accessKeyParameter: string | undefined = undefined) {
    localStorage.removeItem(this.authLocalStorageToken);
    localStorage.removeItem('accessKey');
    CookieComponent.delete('lessonProject');
    CookieComponent.delete('lessonCountry');
    
    // Notify all subscribers that logout occurred
    this.logoutSubject.next();
    
    this.router.navigate(['/auth/login'], {
      queryParams: accessKeyParameter ? { accesskey: accessKeyParameter } : {},
    });
  }

  getUserByToken(): Observable<UserType> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.authToken) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    return this.authHttpService.getUserByToken(auth.authToken).pipe(
      map((user: UserType) => {
        if (user) {
          this.currentUserSubject.next(user);
        } else {
          this.logout();
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // need create new user then login
  registration(user: UserLoggedInModel): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.createUser(user).pipe(
      map(() => {
        this.isLoadingSubject.next(false);
      }),
      switchMap(() => this.login(user.email, user.password)),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  forgotPassword(email: string): Observable<boolean> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotPassword(email)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  // private methods
  private setAuthFromLocalStorage(auth: AuthModel): boolean {
    // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth && auth.authToken) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      return true;
    }
    return false;
  }

  private getAuthFromLocalStorage(): AuthModel | undefined {
    // try {
    //   const lsValue = localStorage.getItem(this.authLocalStorageToken);
    //   if (!lsValue) {
    //     return undefined;
    //   }

    //   const authData = JSON.parse(lsValue);
    //   return authData;
    // } catch (error) {
    //   console.error(error);
    //   return undefined;
    // }
    return undefined;
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
    this.logoutSubject.complete();
  }
}
